import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { UserProfileFull } from '../types/api';

// STD-015: server state for the User Profile domain.
// apiService is imported here (a *.queries.ts file) per STD-016.

export const profileKeys = {
  detail: (userId: string) => ['profile', userId] as const,
};

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

export function useUserProfileQuery(userId: string | undefined) {
  return useQuery<UserProfileFull, Error>({
    queryKey: profileKeys.detail(userId ?? ''),
    enabled: userId != null && userId.length > 0,
    // Component renders inline error state — skip the global toast.
    meta: { skipGlobalErrorToast: true },
    queryFn: async () => {
      const data = await apiService.getProfile(userId!);
      return data;
    },
    // Suppress auto-refresh: user may be mid-edit (bio / name inputs).
    refetchOnWindowFocus: false,
  });
}

// ---------------------------------------------------------------------------
// Mutations — all invalidate the profile detail so the view refreshes.
// Toast messages are surfaced by the calling component (toast.success/error)
// as before; errors are silenced globally.
// ---------------------------------------------------------------------------

export function useUpdateProfileMutation(userId: string) {
  const queryClient = useQueryClient();
  return useMutation<UserProfileFull, Error, Partial<{ bio: string; full_name: string }>>({
    meta: { skipGlobalErrorToast: true },
    mutationFn: (patch) => apiService.updateProfile(userId, patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: profileKeys.detail(userId) }),
  });
}

export interface UploadPictureResult {
  success: boolean;
  error?: string;
}

export function useUploadProfilePictureMutation(userId: string) {
  const queryClient = useQueryClient();
  return useMutation<UploadPictureResult, Error, File>({
    meta: { skipGlobalErrorToast: true },
    mutationFn: (file: File) => apiService.uploadProfilePicture(userId, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: profileKeys.detail(userId) }),
  });
}

export interface DeletePictureResult {
  success: boolean;
  error?: string;
}

export function useDeleteProfilePictureMutation(userId: string) {
  const queryClient = useQueryClient();
  return useMutation<DeletePictureResult, Error, void>({
    meta: { skipGlobalErrorToast: true },
    mutationFn: () => apiService.deleteProfilePicture(userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: profileKeys.detail(userId) }),
  });
}

export interface AddCertificationInput {
  name: string;
  expiry?: string;
  document?: File;
}

export interface CertificationResult {
  success: boolean;
  error?: string;
}

export function useAddCertificationMutation(userId: string) {
  const queryClient = useQueryClient();
  return useMutation<CertificationResult, Error, AddCertificationInput>({
    meta: { skipGlobalErrorToast: true },
    mutationFn: ({ name, expiry, document }: AddCertificationInput) =>
      apiService.addCertification(userId, name, expiry, document),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: profileKeys.detail(userId) }),
  });
}

export function useRemoveCertificationMutation(userId: string) {
  const queryClient = useQueryClient();
  return useMutation<CertificationResult, Error, number>({
    meta: { skipGlobalErrorToast: true },
    mutationFn: (index: number) => apiService.removeCertification(userId, index),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: profileKeys.detail(userId) }),
  });
}
